import React from 'react';
import { Upload, FileText, AlertCircle, ChevronLeft, CheckCircle, XCircle, AlertTriangle, Shield, Clock, TrendingUp, FileCheck, Info, Type, RefreshCw } from 'lucide-react';
import { analyzeLease, LeaseReviewResult, ComplianceIssue } from '../services/leaseReview';
import * as pdfjsLib from 'pdfjs-dist';

if (typeof window !== 'undefined' && 'Worker' in window) {
  try {
    pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
      'pdfjs-dist/build/pdf.worker.mjs',
      import.meta.url
    ).toString();
  } catch (e) {
    pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;
  }
}

interface ReviewLeaseProps {
  onBack: () => void;
}

export const ReviewLease: React.FC<ReviewLeaseProps> = ({ onBack }) => {
  const [file, setFile] = React.useState<File | null>(null);
  const [fileContent, setFileContent] = React.useState<string>('');
  const [error, setError] = React.useState<string>('');
  const [isAnalyzing, setIsAnalyzing] = React.useState(false);
  const [analysis, setAnalysis] = React.useState<LeaseReviewResult | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [loadingProgress, setLoadingProgress] = React.useState<number>(0);
  const [loadingMessage, setLoadingMessage] = React.useState<string>('');
  const [activeTab, setActiveTab] = React.useState<'summary' | 'findings' | 'gaps' | 'recommendations' | 'legal'>('summary');
  const [showTextInput, setShowTextInput] = React.useState(false);
  const [manualText, setManualText] = React.useState<string>('');
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const extractTextFromPDF = async (file: File): Promise<string> => {
    try {
      setLoadingMessage('Reading PDF file...');
      const arrayBuffer = await file.arrayBuffer();

      setLoadingMessage('Loading PDF document...');
      const loadingTask = pdfjsLib.getDocument({
        data: arrayBuffer,
        useWorkerFetch: false,
        isEvalSupported: false,
        useSystemFonts: true,
      });

      const pdf = await loadingTask.promise;
      const totalPages = pdf.numPages;

      if (totalPages === 0) {
        throw new Error('PDF has no pages');
      }

      let fullText = '';

      for (let i = 1; i <= totalPages; i++) {
        setLoadingMessage(`Extracting text from page ${i} of ${totalPages}...`);
        setLoadingProgress((i / totalPages) * 100);

        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();

        const pageText = textContent.items
          .map((item: any) => {
            if ('str' in item) {
              return item.str;
            }
            return '';
          })
          .filter((str: string) => str.trim().length > 0)
          .join(' ');

        if (pageText.trim()) {
          fullText += pageText + '\n\n';
        }
      }

      if (!fullText.trim()) {
        throw new Error('No text content found in PDF. The document may be scanned or image-based.');
      }

      setLoadingMessage('Text extraction complete!');
      return fullText.trim();

    } catch (error: any) {
      console.error('Error extracting text from PDF:', error);

      if (error?.message?.includes('password')) {
        throw new Error('This PDF is password-protected. Please remove the password and try again.');
      }

      if (error?.message?.includes('Invalid PDF')) {
        throw new Error('Invalid or corrupted PDF file. Please try a different file or use the text input option.');
      }

      if (error?.message?.includes('No text content')) {
        throw new Error('This PDF contains no extractable text. It may be a scanned document. Please use the "Paste Text" option instead.');
      }

      throw new Error(error?.message || 'Failed to extract text from PDF. Please try the "Paste Text" option or a different file format.');
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const allowedTypes = ['text/plain', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.some(type => selectedFile.type.includes(type.split('/')[1]))) {
      setError('Please upload a supported file type: .txt, .doc, .docx, or .pdf');
      return;
    }

    if (selectedFile.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }

    setFile(selectedFile);
    setError('');
    setIsLoading(true);

    try {
      let text: string;
      setLoadingProgress(0);

      if (selectedFile.type.includes('pdf')) {
        setLoadingMessage('Processing PDF...');
        text = await extractTextFromPDF(selectedFile);
      } else {
        setLoadingMessage('Reading text file...');
        text = await selectedFile.text();
        setLoadingProgress(100);
      }

      if (!text || text.trim().length === 0) {
        throw new Error('No readable text found in the document. Please try the "Paste Text" option.');
      }

      if (text.trim().length < 100) {
        throw new Error('Document appears to be too short. Please ensure it contains sufficient content.');
      }

      setFileContent(text);
      setAnalysis(null);
      setActiveTab('summary');
      setLoadingMessage('File loaded successfully!');

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error reading file. Please try again.';
      setError(errorMessage);
      console.error('Error reading file:', err);
      setFile(null);
    } finally {
      setIsLoading(false);
      setLoadingProgress(0);
      setLoadingMessage('');
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(droppedFile);

      if (fileInputRef.current) {
        fileInputRef.current.files = dataTransfer.files;
        handleFileChange({ target: { files: dataTransfer.files } } as any);
      }
    }
  };

  const handleManualTextSubmit = () => {
    if (!manualText || manualText.trim().length < 100) {
      setError('Please enter at least 100 characters of text to analyze.');
      return;
    }

    setFileContent(manualText);
    setShowTextInput(false);
    setError('');
  };

  const handleAnalyze = async () => {
    try {
      setIsAnalyzing(true);
      setError('');
      const result = await analyzeLease(fileContent);
      setAnalysis(result);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to analyze the agreement. Please try again.');
      console.error('Analysis error:', err);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const getRiskLevelColor = (level: string) => {
    switch (level) {
      case 'critical': return 'text-red-600 bg-red-50 border-red-200';
      case 'high': return 'text-gray-900 bg-gray-100 border-gray-300';
      case 'medium': return 'text-gray-700 bg-gray-50 border-gray-200';
      case 'low': return 'text-gray-500 bg-gray-50 border-gray-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getComplianceIcon = (status: string) => {
    switch (status) {
      case 'compliant':
        return <CheckCircle className="w-6 h-6 text-gray-900" />;
      case 'non-compliant':
        return <XCircle className="w-6 h-6 text-red-500" />;
      case 'needs-review':
        return <AlertTriangle className="w-6 h-6 text-gray-500" />;
      default:
        return <AlertCircle className="w-6 h-6 text-gray-500" />;
    }
  };

  const renderComplianceIssues = (issues: ComplianceIssue[], severity: string) => {
    if (!issues || issues.length === 0) return null;

    return (
      <div className={`border rounded-lg p-4 ${getRiskLevelColor(severity)}`}>
        <h4 className="font-semibold mb-3 capitalize">{severity} Priority Issues ({issues.length})</h4>
        <div className="space-y-3">
          {issues.map((issue, index) => (
            <div key={index} className="bg-white/50 p-3 rounded border">
              <div className="font-medium mb-1">{issue.clause}</div>
              <div className="text-sm mb-2">{issue.issue}</div>
              <div className="text-sm text-gray-600 mb-2">{issue.explanation}</div>
              <div className="text-sm font-medium">Recommendation: {issue.recommendation}</div>
              {issue.legalReference && (
                <div className="text-xs text-gray-500 mt-1">Legal Reference: {issue.legalReference}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white shadow-xl rounded-lg p-8">
          <button
            onClick={onBack}
            className="mb-6 text-sm text-gray-600 hover:text-gray-900 flex items-center"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back to Home
          </button>

          <div className="flex items-center mb-6">
            <Shield className="w-8 h-8 text-gray-900 mr-3" />
            <h1 className="text-3xl font-bold text-gray-900">
              Rental Reform Bill 2025 Compliance Review
            </h1>
          </div>

          {!fileContent ? (
            <>
              {!showTextInput ? (
                <div
                  className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center hover:border-gray-900 transition-colors"
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    className="hidden"
                    accept=".txt,.doc,.docx,.pdf"
                  />

                  <div className="flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full mb-6 mx-auto">
                    <Upload className="w-10 h-10 text-gray-900" />
                  </div>

                  <h3 className="text-xl font-medium text-gray-900 mb-2">
                    Upload Rental Agreement for Analysis
                  </h3>

                  <p className="text-gray-500 mb-6">
                    Get comprehensive compliance analysis against Rental Reform Bill 2025 requirements
                  </p>

                  {isLoading ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                        <span className="ml-3 text-gray-600">{loadingMessage || 'Processing...'}</span>
                      </div>
                      {loadingProgress > 0 && (
                        <div className="max-w-md mx-auto">
                          <div className="bg-gray-200 rounded-full h-2 overflow-hidden">
                            <div
                              className="bg-gray-900 h-full transition-all duration-300"
                              style={{ width: `${loadingProgress}%` }}
                            />
                          </div>
                          <p className="text-sm text-gray-500 mt-2">{Math.round(loadingProgress)}% complete</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-gray-900 hover:bg-black transition-colors"
                      >
                        <FileText className="w-5 h-5 mr-2" />
                        Select Document
                      </button>

                      <div className="text-gray-500">or</div>

                      <button
                        onClick={() => {
                          setShowTextInput(true);
                          setError('');
                        }}
                        className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                      >
                        <Type className="w-5 h-5 mr-2" />
                        Paste Text Directly
                      </button>
                    </div>
                  )}

                  {error && (
                    <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-4 max-w-2xl mx-auto">
                      <div className="flex items-start">
                        <AlertCircle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
                        <div className="text-left">
                          <p className="text-red-800 font-medium mb-1">Upload Error</p>
                          <p className="text-red-600 text-sm">{error}</p>
                          {error.includes('PDF') && (
                            <div className="mt-3 text-sm text-red-700">
                              <p className="font-medium mb-1">Try these alternatives:</p>
                              <ul className="list-disc list-inside space-y-1">
                                <li>Use the "Paste Text" option to manually enter your document</li>
                                <li>Convert the PDF to a text file first</li>
                                <li>Try a different PDF viewer to export as text</li>
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-500">
                    <div className="flex items-center justify-center">
                      <FileCheck className="w-4 h-4 mr-2" />
                      Supported: PDF, DOC, DOCX, TXT
                    </div>
                    <div className="flex items-center justify-center">
                      <Shield className="w-4 h-4 mr-2" />
                      Secure & Confidential
                    </div>
                    <div className="flex items-center justify-center">
                      <Clock className="w-4 h-4 mr-2" />
                      Analysis in ~30 seconds
                    </div>
                  </div>

                  <div className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-4 max-w-2xl mx-auto text-left">
                    <div className="flex items-start">
                      <Info className="w-5 h-5 text-gray-900 mr-3 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-gray-700">
                        <p className="font-medium mb-2">Having trouble with PDF uploads?</p>
                        <p>Some PDFs (especially scanned documents or those with security settings) may not extract properly. If you encounter issues, use the "Paste Text" option to manually input your agreement text.</p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="border-2 border-gray-300 rounded-lg p-8">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-medium text-gray-900">Paste Agreement Text</h3>
                    <button
                      onClick={() => {
                        setShowTextInput(false);
                        setManualText('');
                        setError('');
                      }}
                      className="text-sm text-gray-600 hover:text-gray-900"
                    >
                      Back to Upload
                    </button>
                  </div>

                  <p className="text-gray-500 mb-4">
                    Copy and paste your rental agreement text below for analysis
                  </p>

                  <textarea
                    value={manualText}
                    onChange={(e) => setManualText(e.target.value)}
                    placeholder="Paste your rental agreement text here..."
                    className="w-full h-64 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-gray-900 text-sm"
                  />

                  <div className="mt-4 flex items-center justify-between">
                    <p className="text-sm text-gray-500">
                      {manualText.length} characters (minimum 100 required)
                    </p>
                    <button
                      onClick={handleManualTextSubmit}
                      disabled={manualText.trim().length < 100}
                      className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-gray-900 hover:bg-black disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Continue to Analysis
                    </button>
                  </div>

                  {error && (
                    <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-center">
                        <AlertCircle className="w-5 h-5 text-red-600 mr-3" />
                        <p className="text-red-800">{error}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="space-y-6">
              <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <FileText className="w-5 h-5 text-gray-900 mr-3" />
                  <div>
                    <span className="font-medium">{file?.name || 'Pasted Text'}</span>
                    <div className="text-sm text-gray-500">
                      {file ? `${Math.round((file.size || 0) / 1024)} KB` : `${fileContent.length} characters`} • Ready for analysis
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setFile(null);
                    setFileContent('');
                    setError('');
                    setAnalysis(null);
                  }}
                  className="text-sm text-gray-600 hover:text-gray-900 px-3 py-1 border border-gray-300 rounded"
                >
                  Upload Different File
                </button>
              </div>

              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                  <span className="ml-3 text-gray-600">Processing document...</span>
                </div>
              ) : !analysis ? (
                <div className="space-y-6">
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                    <h3 className="font-semibold text-gray-900 mb-2">What We'll Analyze:</h3>
                    <ul className="text-sm text-gray-700 space-y-1">
                      <li>Rental Reform Bill 2025 compliance violations</li>
                      <li>Current UK housing law adherence</li>
                      <li>Tenant rights and protection adequacy</li>
                      <li>Landlord obligations and responsibilities</li>
                      <li>Termination procedures and grounds</li>
                      <li>Rent increase limitations and procedures</li>
                      <li>Property condition and safety standards</li>
                    </ul>
                  </div>

                  <div className="flex justify-center">
                    <button
                      onClick={handleAnalyze}
                      disabled={isAnalyzing}
                      className="inline-flex items-center px-8 py-4 border border-transparent text-lg font-medium rounded-md text-white bg-gray-900 hover:bg-black disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      {isAnalyzing ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                          Analyzing Agreement...
                        </>
                      ) : (
                        <>
                          <TrendingUp className="w-5 h-5 mr-3" />
                          Start Compliance Analysis
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-2xl font-bold text-gray-900">Executive Summary</h2>
                      <div className="flex items-center space-x-4">
                        {getComplianceIcon(analysis.executiveSummary.overallCompliance)}
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getRiskLevelColor(analysis.executiveSummary.riskLevel)}`}>
                          {analysis.executiveSummary.riskLevel.toUpperCase()} RISK
                        </span>
                      </div>
                    </div>
                    <p className="text-gray-700 mb-4">{analysis.executiveSummary.summary}</p>
                    {analysis.executiveSummary.keyFindings.length > 0 && (
                      <div>
                        <h3 className="font-semibold mb-2">Key Findings:</h3>
                        <ul className="list-disc list-inside space-y-1 text-gray-700">
                          {analysis.executiveSummary.keyFindings.map((finding, index) => (
                            <li key={index}>{finding}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {analysis.partial && (
                    <div className="bg-gray-50 border border-gray-300 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-gray-800 font-medium">Incomplete Results</p>
                          <p className="text-gray-700 text-sm mt-1">
                            The analysis could not be fully structured. Results shown may be incomplete.
                          </p>
                          <button
                            onClick={() => {
                              setAnalysis(null);
                              setError('');
                              handleAnalyze();
                            }}
                            disabled={isAnalyzing}
                            className="mt-3 inline-flex items-center px-4 py-2 border border-gray-400 text-sm font-medium rounded-md text-gray-800 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            {isAnalyzing ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                                Retrying...
                              </>
                            ) : (
                              <>
                                <RefreshCw className="w-4 h-4 mr-2" />
                                Retry for Full Analysis
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="border-b border-gray-200">
                    <nav className="-mb-px flex space-x-8 overflow-x-auto">
                      {[
                        { id: 'summary', label: 'Summary', icon: FileCheck },
                        { id: 'findings', label: 'Detailed Findings', icon: AlertTriangle },
                        { id: 'gaps', label: 'Compliance Gaps', icon: XCircle },
                        { id: 'recommendations', label: 'Recommendations', icon: CheckCircle },
                        { id: 'legal', label: 'Legal Concerns', icon: Shield },
                      ].map(({ id, label, icon: Icon }) => (
                        <button
                          key={id}
                          onClick={() => setActiveTab(id as any)}
                          className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                            activeTab === id
                              ? 'border-gray-900 text-gray-900'
                              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                          }`}
                        >
                          <Icon className="w-4 h-4 mr-2" />
                          {label}
                        </button>
                      ))}
                    </nav>
                  </div>

                  <div className="space-y-6">
                    {activeTab === 'summary' && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-white border border-gray-200 rounded-lg p-6">
                          <h3 className="font-semibold mb-4">Rental Reform Bill 2025 Impact</h3>
                          <div className="space-y-3">
                            <div>
                              <h4 className="font-medium text-red-600 mb-2">Specific Violations:</h4>
                              <ul className="text-sm space-y-1">
                                {analysis.rentalReformBill2025.specificViolations.map((violation, index) => (
                                  <li key={index} className="flex items-start">
                                    <XCircle className="w-4 h-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                                    {violation}
                                  </li>
                                ))}
                              </ul>
                            </div>
                            <div>
                              <h4 className="font-medium text-gray-700 mb-2">New Requirements:</h4>
                              <ul className="text-sm space-y-1">
                                {analysis.rentalReformBill2025.newRequirements.map((requirement, index) => (
                                  <li key={index} className="flex items-start">
                                    <AlertTriangle className="w-4 h-4 text-gray-500 mr-2 mt-0.5 flex-shrink-0" />
                                    {requirement}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>

                        <div className="bg-white border border-gray-200 rounded-lg p-6">
                          <h3 className="font-semibold mb-4">Priority Actions Required</h3>
                          <div className="space-y-3">
                            <div>
                              <h4 className="font-medium text-red-600 mb-2">Immediate (Critical):</h4>
                              <ul className="text-sm space-y-1">
                                {analysis.recommendations.immediate.map((rec, index) => (
                                  <li key={index} className="flex items-start">
                                    <AlertCircle className="w-4 h-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                                    {rec}
                                  </li>
                                ))}
                              </ul>
                            </div>
                            <div>
                              <h4 className="font-medium text-gray-700 mb-2">Short Term:</h4>
                              <ul className="text-sm space-y-1">
                                {analysis.recommendations.shortTerm.map((rec, index) => (
                                  <li key={index} className="flex items-start">
                                    <Clock className="w-4 h-4 text-gray-500 mr-2 mt-0.5 flex-shrink-0" />
                                    {rec}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {activeTab === 'findings' && (
                      <div className="space-y-6">
                        {renderComplianceIssues(analysis.detailedFindings.critical, 'critical')}
                        {renderComplianceIssues(analysis.detailedFindings.high, 'high')}
                        {renderComplianceIssues(analysis.detailedFindings.medium, 'medium')}
                        {renderComplianceIssues(analysis.detailedFindings.low, 'low')}
                      </div>
                    )}

                    {activeTab === 'gaps' && (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <h3 className="font-semibold text-red-800 mb-3">Missing Provisions</h3>
                          <ul className="text-sm text-red-700 space-y-2">
                            {analysis.complianceGaps.missingProvisions.map((provision, index) => (
                              <li key={index} className="flex items-start">
                                <XCircle className="w-4 h-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                                {provision}
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                          <h3 className="font-semibold text-gray-800 mb-3">Outdated Clauses</h3>
                          <ul className="text-sm text-gray-700 space-y-2">
                            {analysis.complianceGaps.outdatedClauses.map((clause, index) => (
                              <li key={index} className="flex items-start">
                                <AlertTriangle className="w-4 h-4 text-gray-500 mr-2 mt-0.5 flex-shrink-0" />
                                {clause}
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div className="bg-gray-50 border border-gray-300 rounded-lg p-4">
                          <h3 className="font-semibold text-gray-800 mb-3">Inadequate Protections</h3>
                          <ul className="text-sm text-gray-700 space-y-2">
                            {analysis.complianceGaps.inadequateProtections.map((protection, index) => (
                              <li key={index} className="flex items-start">
                                <Shield className="w-4 h-4 text-gray-500 mr-2 mt-0.5 flex-shrink-0" />
                                {protection}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}

                    {activeTab === 'recommendations' && (
                      <div className="space-y-6">
                        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                          <h3 className="font-semibold text-red-800 mb-4">Immediate Actions Required</h3>
                          <ul className="space-y-3">
                            {analysis.recommendations.immediate.map((rec, index) => (
                              <li key={index} className="flex items-start">
                                <AlertCircle className="w-5 h-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                                <span className="text-red-700">{rec}</span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                          <h3 className="font-semibold text-gray-800 mb-4">Short-Term Improvements</h3>
                          <ul className="space-y-3">
                            {analysis.recommendations.shortTerm.map((rec, index) => (
                              <li key={index} className="flex items-start">
                                <Clock className="w-5 h-5 text-gray-500 mr-3 mt-0.5 flex-shrink-0" />
                                <span className="text-gray-700">{rec}</span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div className="bg-gray-50 border border-gray-300 rounded-lg p-6">
                          <h3 className="font-semibold text-gray-800 mb-4">Long-Term Enhancements</h3>
                          <ul className="space-y-3">
                            {analysis.recommendations.longTerm.map((rec, index) => (
                              <li key={index} className="flex items-start">
                                <TrendingUp className="w-5 h-5 text-gray-500 mr-3 mt-0.5 flex-shrink-0" />
                                <span className="text-gray-700">{rec}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}

                    {activeTab === 'legal' && (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <h3 className="font-semibold text-red-800 mb-3">Enforceability Issues</h3>
                          <ul className="text-sm text-red-700 space-y-2">
                            {analysis.legalConcerns.enforceabilityIssues.map((issue, index) => (
                              <li key={index} className="flex items-start">
                                <XCircle className="w-4 h-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                                {issue}
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                          <h3 className="font-semibold text-gray-800 mb-3">Dispute Risks</h3>
                          <ul className="text-sm text-gray-700 space-y-2">
                            {analysis.legalConcerns.disputeRisks.map((risk, index) => (
                              <li key={index} className="flex items-start">
                                <AlertTriangle className="w-4 h-4 text-gray-500 mr-2 mt-0.5 flex-shrink-0" />
                                {risk}
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                          <h3 className="font-semibold text-gray-800 mb-3">Regulatory Violations</h3>
                          <ul className="text-sm text-gray-700 space-y-2">
                            {analysis.legalConcerns.regulatoryViolations.map((violation, index) => (
                              <li key={index} className="flex items-start">
                                <Shield className="w-4 h-4 text-gray-500 mr-2 mt-0.5 flex-shrink-0" />
                                {violation}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {error && (
                <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <AlertCircle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-red-700">{error}</p>
                      <button
                        onClick={handleAnalyze}
                        disabled={isAnalyzing}
                        className="mt-3 inline-flex items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {isAnalyzing ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600 mr-2"></div>
                            Retrying...
                          </>
                        ) : (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Retry Analysis
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
